import React, { useEffect, useState } from 'react';
import { driversApi } from '../lib/api';
import { Skeleton } from './ui/skeleton';
import { PaystubLoadsTable } from './PaystubLoadsTable';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Live (no-PDF) driver paystub breakdown for a week or date range. */
export const DriverPaystubLive = ({ driverId, startDate, endDate }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!driverId || !startDate) return;
    let cancelled = false;
    setLoading(true);
    const end = endDate && endDate !== startDate ? endDate : null;
    driversApi.getPaySummary(driverId, startDate, end)
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [driverId, startDate, endDate]);

  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!summary) return null;

  const stats = [
    { label: 'Loads', value: summary.load_count, testid: 'live-paystub-loads' },
    { label: 'Gross Pay', value: money(summary.gross_pay), testid: 'live-paystub-gross', cls: 'text-emerald-600' },
    { label: 'Deductions', value: `${summary.total_deductions ? '-' : ''}${money(summary.total_deductions)}`, testid: 'live-paystub-deductions', cls: 'text-red-600' },
    { label: 'Net Pay', value: money(summary.net_pay), testid: 'live-paystub-net', cls: 'text-blue-600' },
  ];

  return (
    <div className="space-y-3 border rounded-lg p-3 min-w-0" data-testid="driver-paystub-live">
      <div className="grid grid-cols-4 gap-2 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="text-[10px] uppercase text-muted-foreground">{s.label}</div>
            <div className={`text-sm font-bold font-mono ${s.cls || ''}`} data-testid={s.testid}>{s.value}</div>
          </div>
        ))}
      </div>
      {summary.pay_basis && (
        <p className="text-xs text-muted-foreground" data-testid="live-paystub-pay-basis">
          Pay basis: <span className="font-medium text-foreground">{summary.pay_basis}</span>
          {summary.is_override && <span className="text-amber-700"> · manual override applied</span>}
        </p>
      )}
      {(summary.loads || []).length > 0 && (
        <div className="border rounded max-h-64 overflow-y-auto">
          <PaystubLoadsTable
            loads={summary.loads}
            payType={summary.pay_type}
            payRate={summary.pay_rate}
            showGross={!!summary.show_load_gross}
          />
        </div>
      )}
    </div>
  );
};
